import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ChevronRight, CircleAlert, X, Lock, BadgeCheck } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { ProfileShell } from "../../components/profile/ProfileShell";

const TABS = [
  { id: "personal-info", label: "Personal info", to: "/profile/personal-info" },
  { id: "login-security", label: "Password change", to: "/profile/login-security" },
  { id: "verification", label: "Verification", to: "/profile/verification" },
];

// Module-scope so React keeps the same component type across PersonalInfoPage
// re-renders — otherwise React tears down + recreates each row (and its inner
// DOM, animations, focus) on every parent state change. Pure perf win.
function InfoRow({ label, value, testid, onClick, warning = false, trailingIcon = true, rightLabel = "", locked = false }) {
  return (
    <button
      type="button"
      onClick={locked ? () => toast.info(`${label} is verified and locked. Contact support to change it.`) : onClick}
      data-testid={testid}
      className="w-full flex items-center gap-2 px-4 py-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.03] active:bg-white/[0.06] transition-colors text-left"
    >
      <span className="text-white font-medium text-[15px]">{label}</span>
      <span className="ml-auto flex items-center gap-1.5 max-w-[60%] min-w-0 justify-end">
        {value !== undefined && value !== null && value !== "" ? (
          <span className="text-white/70 text-sm truncate">{value}</span>
        ) : (
          <span className="text-white/45 text-sm">{rightLabel || "Manage"}</span>
        )}
        {locked && <BadgeCheck size={16} className="text-[#21c46e] shrink-0" />}
        {warning && !locked && <CircleAlert size={16} className="text-amber-400 shrink-0" />}
        {locked ? (
          <Lock size={15} className="text-white/35 shrink-0" />
        ) : (
          trailingIcon && <ChevronRight size={18} className="text-white/40 shrink-0" />
        )}
      </span>
    </button>
  );
}

export default function PersonalInfoPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [editing, setEditing] = useState(null); // { field, label, type, value }
  // Stable per-browser device fingerprint (same value the API interceptor sends
  // as X-Device-Id). Read once; create it if missing so it's always shown.
  const [deviceId] = useState(() => {
    try {
      let id = localStorage.getItem("ml_device_id");
      if (!id) {
        id =
          (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
          `d_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("ml_device_id", id);
      }
      return id;
    } catch {
      return "";
    }
  });

  const copyUsername = async () => {
    const ok = await copyToClipboard(user?.username || "");
    if (ok) toast.success("Username copied");
    else toast.error("Could not copy");
  };

  const startEdit = (field, label, type = "text") =>
    setEditing({ field, label, type, value: user?.[field] || "" });

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put("/auth/me", { [editing.field]: editing.value });
      await refreshUser();
      toast.success(`${editing.label} updated`);
      setEditing(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <ProfileShell title="Profile">
      <div className="py-2 pb-10" data-testid="personal-info-page">
        {/* Tabs */}
        <div
          className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pt-1 pb-3"
          style={{ scrollbarWidth: "none" }}
        >
          {TABS.map((t) => {
            const active = location.pathname === t.to;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`tab-${t.id}`}
                onClick={() => navigate(t.to)}
                className={`h-10 px-4 rounded-lg whitespace-nowrap text-sm font-semibold transition-all ${
                  active
                    ? "bg-white/[0.08] text-white border border-white/10"
                    : "text-white/55 hover:text-white/80"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Rows */}
        <div className="rounded-xl bg-[#101824] border border-white/5 overflow-hidden">
          <div
            className="w-full flex items-center gap-2 px-4 py-4 border-b border-white/5"
            data-testid="row-username"
          >
            <span className="text-white font-medium text-[15px]">Username</span>
            <span className="ml-auto flex items-center gap-1.5 max-w-[60%] min-w-0 justify-end">
              <span className="text-white/70 text-sm truncate">{user?.username}</span>
              <button
                type="button"
                onClick={copyUsername}
                aria-label="copy username"
                data-testid="copy-username"
                className="p-1 text-white/55 hover:text-white"
              >
                <Copy size={15} />
              </button>
            </span>
          </div>

          {user?.player_id && (
            <div
              className="w-full flex items-center gap-2 px-4 py-4 border-b border-white/5"
              data-testid="row-player-id"
            >
              <span className="text-white font-medium text-[15px]">Player ID</span>
              <span className="ml-auto flex items-center gap-1.5 max-w-[60%] min-w-0 justify-end">
                <span className="text-amber-300 text-sm font-mono tabular-nums tracking-wider">{user.player_id}</span>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await copyToClipboard(user.player_id);
                    if (ok) toast.success("Player ID copied");
                    else toast.error("Could not copy");
                  }}
                  aria-label="copy player id"
                  data-testid="copy-player-id"
                  className="p-1 text-white/55 hover:text-white"
                >
                  <Copy size={15} />
                </button>
              </span>
            </div>
          )}

          <div
            className="w-full flex items-center gap-2 px-4 py-4 border-b border-white/5"
            data-testid="row-device-id"
          >
            <span className="text-white font-medium text-[15px]">Device ID</span>
            <span className="ml-auto flex items-center gap-1.5 max-w-[60%] min-w-0 justify-end">
              <span className="text-sky-300 text-sm font-mono truncate select-all" title={deviceId} data-testid="device-id-value">
                {deviceId || "—"}
              </span>
              <button
                type="button"
                onClick={async () => {
                  const ok = await copyToClipboard(deviceId);
                  if (ok) toast.success("Device ID copied");
                  else toast.error("Could not copy");
                }}
                aria-label="copy device id"
                data-testid="copy-device-id"
                className="p-1 text-white/55 hover:text-white"
              >
                <Copy size={15} />
              </button>
            </span>
          </div>

          <InfoRow
            label="Full legal name"
            value={user?.full_name}
            testid="row-full-name"
            onClick={() => startEdit("full_name", "Full legal name")}
          />
          <InfoRow
            label="Date of birth"
            value={user?.date_of_birth}
            testid="row-dob"
            onClick={() => startEdit("date_of_birth", "Date of birth", "date")}
          />
          <InfoRow
            label="Phone"
            value={user?.phone ? `${user.country_code || ""} ${user.phone}` : ""}
            testid="row-phone"
            warning={user?.phone && !user?.phone_verified}
            locked={!!user?.phone_verified}
            onClick={() => startEdit("phone", "Phone")}
          />
          <InfoRow
            label="Email"
            value={user?.email}
            testid="row-email"
            warning={user?.email && !user?.email_verified}
            locked={!!user?.email_verified}
            onClick={() => startEdit("email", "Email", "email")}
          />
        </div>

        <p className="text-white/40 text-xs text-center mt-4 leading-relaxed px-4">
          Keeping your profile up to date helps secure your account and speed up
          withdrawals.
        </p>
      </div>

      {/* Edit sheet */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setEditing(null)}
          data-testid="edit-sheet"
        >
          <div
            className="w-full sm:max-w-md bg-[#101824] rounded-t-2xl sm:rounded-2xl border border-white/10 p-5 pb-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">
                Update {editing.label}
              </h3>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="h-9 w-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5"
                aria-label="close"
              >
                <X size={18} />
              </button>
            </div>
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-white/55">
                {editing.label}
              </span>
              <input
                type={editing.type}
                data-testid="edit-sheet-input"
                value={editing.value || ""}
                onChange={(e) =>
                  setEditing({ ...editing, value: e.target.value })
                }
                className="mt-1 w-full h-11 rounded-lg bg-[#0e1620] border border-white/10 px-3 text-white focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/25"
                autoFocus
              />
            </label>
            <button
              type="button"
              data-testid="edit-sheet-save"
              onClick={saveEdit}
              className="w-full h-12 mt-4 rounded-lg text-white font-bold"
              style={{
                background:
                  "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                boxShadow:
                  "0 4px 14px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              Save changes
            </button>
          </div>
        </div>
      )}
    </ProfileShell>
  );
}
